import React, { useMemo, useState } from 'react';
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import LinkIcon from '@mui/icons-material/Link';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import { toast } from 'react-toastify';

const ProblemShareButton = ({ slug, title, size = 'small', sx }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return `/problems/${slug}`;
    return `${window.location.origin}/problems/${slug}`;
  }, [slug]);

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const close = () => setAnchorEl(null);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Problem link copied');
    } catch {
      toast.error('Could not copy link');
    }
    close();
  };

  const nativeShare = async () => {
    if (!canNativeShare) {
      await copyLink();
      return;
    }
    try {
      await navigator.share({
        title: title ? `${title} · CrafterHack` : 'CrafterHack Problem',
        text: title ? `Try solving "${title}" on CrafterHack.` : 'Try this problem on CrafterHack.',
        url: shareUrl,
      });
    } catch (err) {
      if (err?.name !== 'AbortError') await copyLink();
    }
    close();
  };

  return (
    <>
      <Tooltip title="Share problem">
        <IconButton
          size={size}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Share problem"
          sx={sx}
        >
          <ShareOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 168,
              mt: 0.5,
              borderRadius: 2,
            },
          },
        }}
      >
        <MenuItem onClick={copyLink}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600 }}>
            Copy link
          </ListItemText>
        </MenuItem>
        {canNativeShare && (
          <MenuItem onClick={nativeShare}>
            <ListItemIcon>
              <IosShareOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600 }}>
              Share…
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ProblemShareButton;
